import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';

import {Constants} from '../config/constants.config';
import {GetRecipesDTO} from '../dto/getRecipes.dto';
import {RemoveRecipeFromDiaryDTO} from '../dto/removeRecipeFromDiary.dto';
import {DiaryChatMessages, DiaryPassedRecipeDeleteDto, DiaryPassedRecipeViewModel, DiaryViewModel} from '../viewModels/diary.viewModel';
import {CreateDiaryDTO} from '../dto/createDiary.dto';
import {AddPassedRecipeDto, AddRecipeToDiaryDTO} from '../dto/addRecipeToDiary.dto';
import {formatDate} from '../utilities/date.utilities';
import {CalculationsViewModel} from '../viewModels/calculations.viewModel';
import {UpdateDiaryDTO} from '../dto/updateDiary.dto';
import {MarkDiaryMealAsEatenDTO} from '../dto/markDiaryMealAsEaten.dto';
import {MealRecipeViewModel} from '../viewModels/mealRecipe.viewModel';
import {RecipeViewModalModel, RecipeViewModel} from '../viewModels/recipe.viewModel';
import {DiaryNutrientsResponseDTO} from '../dto/diaryNutrientsResponse.dto';
import {GetMealLeftCaloriesRequestDTO} from '../dto/getMealLeftCaloriesRequest.dto';
import {GetMealLeftCaloriesResponseDTO} from '../dto/getMealLeftCaloriesResponse.dto';
import {ClearDiaryRecipesDto} from '../dto/clearDiaryRecipes.dto';
import {ExternalMealViewModel} from '../../meals/components/viewModels/externalMeal.viewModel';
import {UpdateDiaryExternalRecipesDto} from '../dto/updateDiaryExternalRecipes.dto';
import {RecipeCategoryViewModel} from '../viewModels/recipeCategory.viewModel';
import {MealViewModel} from '../viewModels/meal.viewModel';
import {MarkShoppingItemAsDeletedDto} from '../dto/markShoppingItemAsDeleted.dto';
import {DiaryLoadRecipesDTO} from '../dto/diaryLoadRecipes.dto';
import {FriendsDiaryDTO} from '../dto/friendsDiary.dto';
import {DiaryAdditionalTrainingViewModel, DiaryTrainingViewModel, TrainingViewModel} from '../viewModels/training.viewModel';
import {moment} from 'ngx-bootstrap/chronos/test/chain';
import {DiaryProductChangeViewModel} from '../viewModels/product.viewModel';
import {CreateVmvOrderResponseViewModel, CreateVmvOrderViewModel} from '../viewModels/vmvCreateOrder.viewModel';
import {IsUserMasterReadyProfile} from '../viewModels/master.viewModel';

@Injectable()
export class DiaryService {
    constructor(private readonly httpClient: HttpClient, private readonly constants: Constants) {
    }

    createDiary(date: string = formatDate(new Date(), this.constants.defaultAPIDateFormat), createDiaryDTO: CreateDiaryDTO): Observable<void> {
        return this.httpClient.post<void>(`${this.constants.apiURL()}/diary/${date}/`, createDiaryDTO);
    }

    updateDiary(date: string = formatDate(new Date(), this.constants.defaultAPIDateFormat), updateDiaryDTO: UpdateDiaryDTO): Observable<DiaryViewModel> {
        return this.httpClient.put<DiaryViewModel>(`${this.constants.apiURL()}/diary/${date}/`, updateDiaryDTO);
    }

    getDiary(userId: number, date: string = formatDate(new Date(), this.constants.defaultAPIDateFormat)): Observable<DiaryViewModel> {
        //diary/user/34/date/2023-02-25
        return this.httpClient.get<DiaryViewModel>(`${this.constants.dataURL()}/diary/user/${userId}/date/${date}/`);
    }

    getDiaryMacros(date: string = formatDate(new Date(), this.constants.defaultAPIDateFormat)): Observable<CalculationsViewModel> {
        return this.httpClient.get<CalculationsViewModel>(`${this.constants.apiURL()}/diary/recipes/macros/${date}/`);
    }

    getRecipes(getRecipesDTO: GetRecipesDTO): Observable<MealRecipeViewModel[]> {
        return this.httpClient.post<MealRecipeViewModel[]>(this.constants.apiURL() + '/diary/recipes/', getRecipesDTO);
    }

    getNewRecipes(getRecipesDTO: GetRecipesDTO): Observable<MealRecipeViewModel[]> {
        // return this.httpClient.post<MealRecipeViewModel[]>(`http://localhost:8080/`, getRecipesDTO);
        return this.httpClient.post<MealRecipeViewModel[]>(`${this.constants.recipeURL()}`, getRecipesDTO);
    }

    getRecipesCategories(meal: MealViewModel): Observable<RecipeCategoryViewModel[]> {
        return this.httpClient.get<RecipeCategoryViewModel[]>(`${this.constants.apiURL()}/recipes/categories/${meal.id}/`);
    }

    addRecipeToDiary(addRecipeToDiaryDTO: AddRecipeToDiaryDTO): Observable<void> {
        return this.httpClient.put<void>(`${this.constants.apiURL()}/diary/recipes/`, addRecipeToDiaryDTO);
    }

    removeRecipeFromDiary(removeRecipeFromDiaryDTO: RemoveRecipeFromDiaryDTO): Observable<void> {
        return this.httpClient.request<void>('delete', `${this.constants.apiURL()}/diary/recipes/`, {
            body: removeRecipeFromDiaryDTO
        });
    }

    markDiaryMealAsEaten(markDiaryMealAsEatenDTO: MarkDiaryMealAsEatenDTO): Observable<void> {
        return this.httpClient.post<void>(`${this.constants.apiURL()}/diary/recipes/eaten/`, markDiaryMealAsEatenDTO);
    }

    getRecipe(id: number, diary_recipe_id: number): Observable<RecipeViewModalModel> {
        return this.httpClient.post<RecipeViewModalModel>(`${this.constants.apiURL()}/recipes/${id}`, {
            diary_recipe_id: diary_recipe_id
        });
    }

    getDiaryNutrients(date: string = formatDate(new Date(), this.constants.defaultAPIDateFormat)): Observable<DiaryNutrientsResponseDTO> {
        return this.httpClient.get<DiaryNutrientsResponseDTO>(`${this.constants.apiURL()}/diary/nutrients/${date}/`);
    }

    getMealLeftCalories(getMealLeftCaloriesRequestDTO: GetMealLeftCaloriesRequestDTO): Observable<GetMealLeftCaloriesResponseDTO> {
        return this.httpClient.post<GetMealLeftCaloriesResponseDTO>(`${this.constants.apiURL()}/diary/meal/left-calories/`, getMealLeftCaloriesRequestDTO);
    }

    clearDiaryMealRecipes(clearDiaryRecipesDTO: ClearDiaryRecipesDto, meal: MealViewModel): Observable<void> {
        return this.httpClient.delete<void>(`${this.constants.apiURL()}/diary/meal/delete/${clearDiaryRecipesDTO.date}/${meal.id}/`);
    }

    getUserMacros(date: string = formatDate(new Date(), this.constants.defaultAPIDateFormat)): Observable<CalculationsViewModel> {
        return this.httpClient.get<CalculationsViewModel>(`${this.constants.apiURL()}/diary/macros/${date}/`);
    }

    getShoppingList(
        fromDate: string = formatDate(new Date(), this.constants.defaultAPIDateFormat),
        toDate: string = formatDate(new Date(), this.constants.defaultAPIDateFormat),
        friends: string
    ): Observable<DiaryNutrientsResponseDTO> {
        return this.httpClient.get<DiaryNutrientsResponseDTO>(`${this.constants.apiURL()}/diary/shopping-list/${fromDate}/${toDate}/?friends=${friends}`);
    }

    markShoppingItemAsDeleted(dataDto: MarkShoppingItemAsDeletedDto, fromDate: string = formatDate(new Date(), this.constants.defaultAPIDateFormat), toDate: string = formatDate(new Date(), this.constants.defaultAPIDateFormat)): Observable<void> {
        return this.httpClient.post<void>(`${this.constants.apiURL()}/diary/shopping-list/${fromDate}/${toDate}/`, dataDto);
    }

    clearShoppingItems(fromDate: string = formatDate(new Date(), this.constants.defaultAPIDateFormat), toDate: string = formatDate(new Date(), this.constants.defaultAPIDateFormat)): Observable<void> {
        return this.httpClient.delete<void>(`${this.constants.apiURL()}/diary/shopping-list/${fromDate}/${toDate}/`);
    }

    getDiaryExternalRecipes(date: string = formatDate(new Date(), this.constants.defaultAPIDateFormat)): Observable<ExternalMealViewModel[]> {
        return this.httpClient.get<ExternalMealViewModel[]>(`${this.constants.apiURL()}/diary/external-recipes/${date}/`);
    }

    updateDiaryExternalRecipes(updateDiaryExternalRecipesDto: UpdateDiaryExternalRecipesDto, date: string = formatDate(new Date(), this.constants.defaultAPIDateFormat)): Observable<void> {
        return this.httpClient.post<void>(`${this.constants.apiURL()}/diary/external-recipes/${date}/`, updateDiaryExternalRecipesDto);
    }

    recalculateExistingRecipes(date: string = formatDate(new Date(), this.constants.defaultAPIDateFormat)): Observable<void> {
        return this.httpClient.post<void>(`${this.constants.apiURL()}/diary/recalculate/meals/${date}/`, {});
    }

    loadRecipes(diaryLoadRecipesDTO: DiaryLoadRecipesDTO): Observable<void> {
        return this.httpClient.post<void>(`${this.constants.apiURL()}/diary/auto/recipes/`, diaryLoadRecipesDTO);
    }

    getFriendsDiaryRecipes(friendsDiaryDTO: FriendsDiaryDTO): Observable<any> {
        return this.httpClient.post<any>(`${this.constants.apiURL()}/diary/friend/recipes/`, friendsDiaryDTO);
    }

    sendShoppingListAccess(
        email: string,
        fromDate: string = formatDate(new Date(), this.constants.defaultAPIDateFormat),
        toDate: string = formatDate(new Date(), this.constants.defaultAPIDateFormat)
    ): Observable<void> {
        return this.httpClient.post<void>(`${this.constants.apiURL()}/diary/shopping-list-access/${fromDate}/${toDate}/`, {
            'email': email
        });
    }

    copyDiaryRecipe(
        diary_date: string = formatDate(new Date(), this.constants.defaultAPIDateFormat),
        diary_recipe_id: number,
        copy_to_dates: string[],
        copy_to_meal: number
    ): Observable<void> {
        return this.httpClient.post<void>(`${this.constants.apiURL()}/diary/recipes/copy/${diary_date}/`, {
            'diary_recipe_id': diary_recipe_id,
            'copy_to_dates': copy_to_dates,
            'copy_to_meal': copy_to_meal,
        });
    }

    copyAllDiaryRecipes(
        diary_date: string = formatDate(new Date(), this.constants.defaultAPIDateFormat),
        copy_to_dates: string[]
    ): Observable<void> {
        return this.httpClient.post<void>(`${this.constants.apiURL()}/diary/recipes/all/copy/${diary_date}/`, {
            'copy_to_dates': copy_to_dates
        });
    }

    copyAllDiaryRecipesToInterval(
        fromDate: string = formatDate(new Date(), this.constants.defaultAPIDateFormat),
        toDate: string = formatDate(new Date(), this.constants.defaultAPIDateFormat),
        copy_to_dates: string[]
    ): Observable<void> {
        return this.httpClient.post<void>(`${this.constants.apiURL()}/diary/recipes/all/copy/interval/`, {
            'fetch_dates': copy_to_dates,
            'from_date': fromDate,
            'to_date': toDate
        });
    }

    getDiaryTrainings(diary_date: string = formatDate(new Date(), this.constants.defaultAPIDateFormat)): Observable<DiaryTrainingViewModel[]> {
        return this.httpClient.get<DiaryTrainingViewModel[]>(`${this.constants.apiURL()}/diary/trainings/` + diary_date + `/`);
    }

    updateDiaryTrainings(diary_date: string = formatDate(new Date(), this.constants.defaultAPIDateFormat), data): Observable<void> {
        return this.httpClient.put<void>(`${this.constants.apiURL()}/diary/trainings/` + diary_date + `/`, data);
    }

    updateDiaryDisableTraining(is_disable: boolean, diary_date: string = formatDate(new Date(), this.constants.defaultAPIDateFormat)): Observable<void> {
        return this.httpClient.put<void>(`${this.constants.apiURL()}/diary/disable/trainings/` + diary_date + `/`, {
            'is_disable': is_disable
        });
    }

    getAdditionalDiaryTrainings(diary_date: string = formatDate(new Date(), this.constants.defaultAPIDateFormat)): Observable<DiaryAdditionalTrainingViewModel> {
        return this.httpClient.get<DiaryAdditionalTrainingViewModel>(`${this.constants.apiURL()}/diary/additional/trainings/` + diary_date + `/`);
    }

    updateDiaryAdditionalTrainings(diary_date: string = formatDate(new Date(), this.constants.defaultAPIDateFormat), data): Observable<void> {
        return this.httpClient.put<void>(`${this.constants.apiURL()}/diary/additional/trainings/` + diary_date + `/`, data);
    }

    getSuggestionProductsToChange(diary_recipe_id: number, change_diary_product_id: number): Observable<DiaryProductChangeViewModel[]> {
        return this.httpClient.post<DiaryProductChangeViewModel[]>(`${this.constants.apiURL()}/diary/recipe/change/product/`, {
            'diary_recipe_id': diary_recipe_id,
            'change_diary_product_id': change_diary_product_id
        });
    }

    changeDiaryRecipeProduct(diary_recipe_id: number, change_diary_product_id: number, change_to_product_id: number, weight: number, qty: number): Observable<void> {
        return this.httpClient.put<void>(`${this.constants.apiURL()}/diary/recipe/change/product/`, {
            'diary_recipe_id': diary_recipe_id,
            'change_diary_product_id': change_diary_product_id,
            'change_to_product_id': change_to_product_id,
            'weight': weight,
            'qty': qty
        });
    }

    removeProductFromDiaryRecipe(diary_date: string = formatDate(new Date(), this.constants.defaultAPIDateFormat), product_id: number): Observable<void> {
        return this.httpClient.request<void>('delete', `${this.constants.apiURL()}/diary/recipe/product/`, {
            body: {
                diary_date: diary_date,
                product_id: product_id
            }
        });
    }

    getChatMessages(receiver_id: number): Observable<DiaryChatMessages> {
        return this.httpClient.get<DiaryChatMessages>(`${this.constants.apiURL()}/diary/chat/${receiver_id}/`);
    }

    postChatMessages(receiver_id: number, message: string): Observable<void> {
        return this.httpClient.post<void>(`${this.constants.apiURL()}/diary/chat/${receiver_id}/`, {
            'message': message
        });
    }

    deleteAllDiaryRecipes(delete_dates: string[]): Observable<void> {
        return this.httpClient.post<void>(`${this.constants.apiURL()}/diary/recipes/all/delete/`, {
            'delete_dates': delete_dates
        });
    }

    deleteAllDiaryRecipesFromToday(diary_date: string = formatDate(new Date(), this.constants.defaultAPIDateFormat)): Observable<void> {
        return this.httpClient.post<void>(`${this.constants.apiURL()}/diary/recipes/all/delete/from/now/`, {
            diary_date: diary_date
        });
    }

    // MASTER
    getMasterChatMessages(receiver_id: number): Observable<DiaryChatMessages> {
        return this.httpClient.get<DiaryChatMessages>(`${this.constants.apiURL()}/diary/chat/master/${receiver_id}/`);
    }

    postMasterChatMessages(receiver_id: number, sender_id: number, message: string): Observable<void> {
        return this.httpClient.post<void>(`${this.constants.apiURL()}/diary/chat/master/${receiver_id}/`, {
            'message': message,
            'sender_id': sender_id
        });
    }

    getUserIsMasterReady(): Observable<IsUserMasterReadyProfile> {
        return this.httpClient.get<IsUserMasterReadyProfile>(`${this.constants.apiURL()}/user/is-master-ready`);
    }

    createVmvOrder(data: CreateVmvOrderViewModel): Observable<CreateVmvOrderResponseViewModel> {
        return this.httpClient.post<CreateVmvOrderResponseViewModel>(`${this.constants.dataURL()}/v2/external/create-order/vmv`, data);
    }

    addRecipeInDiaryPassed(addPassedRecipeDto: AddPassedRecipeDto): Observable<void> {
        return this.httpClient.post<void>(`${this.constants.apiURL()}/diary/passed/recipes/`, addPassedRecipeDto);
    }

    getDiaryPassedRecipes(diaryId): Observable<DiaryPassedRecipeViewModel[]> {
        return this.httpClient.get<DiaryPassedRecipeViewModel[]>(`${this.constants.apiURL()}/diary/passed/recipes/?diary_id=${diaryId}`);
    }

    removeRecipeFromDiaryPassed(data: DiaryPassedRecipeDeleteDto) {
        return this.httpClient.put<void>(`${this.constants.apiURL()}/diary/passed/recipes/`, data);
    }
}
